import {
  useContext, useEffect, useMemo, useState,
} from "react";
import { useForm, useWatch, FormProvider } from "react-hook-form";
import MainContext from "../../context/main/mainContext";
import CollapsibleSection from "../../components/CollapsibleSection";
import SeatsConfig from "./SeatsConfig";
import EnrichmentConfig from "./EnrichmentConfig";
import DedicatedCsmConfig from "./DedicatedCsmConfig";
import ActionsBar from "./ActionBar";
import StripeLinks from "./StripeLinks";
import money from "../../utilities/money";
import PreviewUpdatesSidebar from "./PreviewUpdatesSideBar";
import { getPercentOffInfo } from "../../utilities/percentOff";
import InfoBadge from "../../components/InfoBadge";

const initialForm = {
  seats: {
    paid: 5,
    free: 0,
    freeSeatsEnabled: true,
    dollars: 48,
    hasCustomPricing: false,
  },
  enrichment: {
    enabled: false,
    credits: 500,
    dollars: 12,
    hasCustomPricing: false,
    level: "PREMIUM",
    options: {
      hasListEnrichment: true,
      hasFreeListEnrichment: false,
      hasEventIntelligence: true,
    },
  },
  dedicatedCsm: {
    enabled: false,
    hasCustomPricing: false,
    dollars: 1000,
  },
  billing: {
    generateInvoice: false,
    prorationEnabled: true,
  },
};

export default function TeamSubscriptionUpdatesForm({
  onOpenStripePaymentModal: handleOpenStripePaymentModal,
}) {
  const mainContext = useContext(MainContext);
  const team = mainContext?.team?.team || null;
  const {
    stripe_customer: stripeCustomer,
    previewSubscriptionUpdates,
    updateTeamSubscription,
    getSubscriptionFormPrefillForTeam,
  } = mainContext || {};

  const isUpdateMode = Boolean(team?.stripeSubscription);

  const methods = useForm({
    defaultValues: initialForm,
    mode: "onChange",
  });
  const {
    control, handleSubmit, reset, register, setValue,
  } = methods;

  const form = useWatch({ control });

  const [isLoading, setIsLoading] = useState(false);
  const [previewInfo, setPreviewInfo] = useState({});
  const [isLoadingPreview, setIsLoadingPreview] = useState(false);
  const [generatedInvoice, setGeneratedInvoice] = useState({});

  const [sectionsExpanded, setSectionsExpanded] = useState({
    seats: !isUpdateMode,
    enrichment: !isUpdateMode,
    dedicatedCsm: !isUpdateMode,
  });

  useEffect(() => {
    (async () => {
      if (!team?.id || !stripeCustomer?.id || !stripeCustomer?.subscriptions?.data?.length) return;
      const data = await getSubscriptionFormPrefillForTeam(team.id);
      reset(data);
    })();
  }, [team?.id]);

  const toggleSection = (section) => {
    setSectionsExpanded((prev) => ({ ...prev, [section]: !prev[section] }));
  };

  const payload = useMemo(() => {
    const f = form || {};
    return {
      seats: {
        ...f.seats && {
          paid: Number(f.seats?.paid) || 0,
          ...(f.seats?.freeSeatsEnabled ? { free: Number(f.seats?.free ?? 0) } : undefined),
          ...(f.seats?.hasCustomPricing && { unitAmount: money.dollarsToCents(f.seats.dollars) }),
        },
      },
      ...(f.enrichment
        && f.enrichment.enabled && {
        enrichment: {
          credits: Number(f.enrichment.credits) || 0,
          ...(f.enrichment.hasCustomPricing && {
            unitAmount: money.dollarsToCents(f.enrichment.dollars),
          }),
          level: f.enrichment.level || "PREMIUM",
          options: {
            hasListEnrichment: Boolean(f.enrichment.options?.hasListEnrichment),
            hasFreeListEnrichment: Boolean(f.enrichment.options?.hasFreeListEnrichment),
            hasEventIntelligence: Boolean(f.enrichment.options?.hasEventIntelligence),
          },
        },
      }),
      ...(f.dedicatedCsm
        && f.dedicatedCsm.enabled && {
        dedicatedCsm: {
          ...(f.dedicatedCsm.hasCustomPricing && {
            unitAmount: money.dollarsToCents(f.dedicatedCsm.dollars),
          }),
        },
      }),
      billing: {
        generateInvoice: Boolean(f.billing?.generateInvoice),
        prorationEnabled: Boolean(f.billing?.prorationEnabled),
      },
    };
  }, [form]);

  useEffect(() => {
    if (typeof previewSubscriptionUpdates !== "function") return undefined;
    const controller = new AbortController();
    setIsLoadingPreview(true);

    const timeout = setTimeout(async () => {
      try {
        const preview = await previewSubscriptionUpdates(
          team.id,
          { ...payload, seats: { ...payload.seats, billingType: "BILLED_QUANTITY" } },
          controller.signal,
        );
        setPreviewInfo(preview);
      } catch (err) {
        if (err?.name !== "AbortError") {
          // ignore other preview failures for now
        }
      } finally {
        setIsLoadingPreview(false);
      }
    }, 1000);

    return () => {
      clearTimeout(timeout);
      controller.abort();
    };
  }, [payload.seats, payload.enrichment, payload.dedicatedCsm, team?.id]);

  const onSubmit = async () => {
    setIsLoading(true);
    if (!team || !team.id || typeof updateTeamSubscription !== "function") {
      setIsLoading(false);
      return;
    }

    try {
      const res = await updateTeamSubscription(team.id, {
        ...payload,
        seats: { ...payload.seats, billingType: "BILLED_QUANTITY" },
      });
      setGeneratedInvoice(res?.invoice || {});
    } finally {
      setIsLoading(false);
    }
  };

  const handleReset = () => {
    reset(initialForm);
  };

  const inputClasses = "mt-1.5 w-full border border-gray-300 rounded-lg px-3.5 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-gray-900 focus:border-transparent transition-all";
  const labelClasses = "block text-sm font-medium text-gray-900";
  const helperClasses = "text-xs text-gray-500 mt-1.5";

  return (
    <div className="min-h-screen bg-gray-50 py-8 px-4 sm:px-6 lg:px-8">
      <div className="max-w-7xl mx-auto">
        <FormProvider {...methods}>
          <form onSubmit={handleSubmit(onSubmit)}>
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              <div className="lg:col-span-2 space-y-6">
                <CollapsibleSection
                  title="Enrichment Configuration"
                  isExpanded={sectionsExpanded.enrichment}
                  onToggle={() => toggleSection("enrichment")}
                  info={(() => {
                    const info = getPercentOffInfo(form?.enrichment?.percentOff);
                    return info ? <InfoBadge {...info} /> : null;
                  })()}
                >
                  <EnrichmentConfig
                    inputClasses={inputClasses}
                    labelClasses={labelClasses}
                    helperClasses={helperClasses}
                  />
                </CollapsibleSection>

                <CollapsibleSection
                  title="Seat Configuration"
                  isExpanded={sectionsExpanded.seats}
                  onToggle={() => toggleSection("seats")}
                  info={(() => {
                    const info = getPercentOffInfo(form?.seats?.percentOff);
                    return info ? <InfoBadge {...info} /> : null;
                  })()}
                >
                  <SeatsConfig
                    isCollapsible={true}
                    isUpgrade={isUpdateMode}
                    inputClasses={inputClasses}
                    labelClasses={labelClasses}
                    helperClasses={helperClasses}
                  />
                </CollapsibleSection>

                <CollapsibleSection
                  title="Dedicated CSM Configuration"
                  isExpanded={sectionsExpanded.dedicatedCsm}
                  onToggle={() => toggleSection("dedicatedCsm")}
                  info={(() => {
                    const info = getPercentOffInfo(form?.dedicatedCsm?.percentOff);
                    return info ? <InfoBadge {...info} /> : null;
                  })()}
                >
                  <DedicatedCsmConfig inputClasses={inputClasses} />
                </CollapsibleSection>

                <CollapsibleSection
                  title="Options"
                  isExpanded={sectionsExpanded.options}
                  onToggle={() => toggleSection("options")}
                >
                  <div className="flex flex-col items-start gap-4">
                    {/* Generate invoice checkbox */}
                    <div className="flex items-center gap-2">
                      <label htmlFor="billing-generate-invoice" className={labelClasses}>
                        Generate Invoice
                      </label>
                      <input
                        {...register("billing.generateInvoice")}
                        id="billing-generate-invoice"
                        type="checkbox"
                        checked={form.billing?.generateInvoice}
                        onChange={(e) => {
                          const value = e.target.checked;
                          setValue("billing.generateInvoice", value, { shouldDirty: true });
                          if (!value) {
                            setValue("billing.prorationEnabled", false, { shouldDirty: true });
                          }
                        }}
                        className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                      />
                    </div>
                    {/* Proration enabled checkbox, only show if generateInvoice is checked */}
                    {form.billing?.generateInvoice && (
                      <div className="flex items-center gap-2">
                        <label htmlFor="proration-enabled" className={labelClasses}>
                          Enable Proration
                        </label>
                        <input
                          {...register("billing.prorationEnabled")}
                          id="proration-enabled"
                          type="checkbox"
                          checked={form.billing?.prorationEnabled}
                          onChange={(e) => setValue("billing.prorationEnabled", e.target.checked, {
                            shouldDirty: true,
                          })}
                          className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                        />
                      </div>
                    )}
                  </div>
                </CollapsibleSection>

                <ActionsBar
                  isLoading={isLoading}
                  onReset={handleReset}
                  onSubmitText={isUpdateMode ? "Update Subscription" : "Create Subscription"}
                  onOpenStripePaymentModal={handleOpenStripePaymentModal}
                />
              </div>
              <div>
                <PreviewUpdatesSidebar
                  isLoadingPreview={isLoadingPreview}
                  previewInfo={previewInfo}
                  isUpdateMode={isUpdateMode}
                />
                <StripeLinks
                  team={team}
                  stripeCustomer={stripeCustomer}
                  invoice={generatedInvoice}
                />
              </div>
            </div>
          </form>
        </FormProvider>
      </div>
    </div>
  );
}
